import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, UserPlus } from 'lucide-react'
import {
  getProjectBillingConfig, updateProjectBillingConfig, type ProjectBillingConfig,
} from '@/api/billing'
import Button from '@/components/common/Button'

/**
 * Self-registration monetization for the application's public sign-up.
 *
 * All prices are MONEY in the application's own currency (INR/USD — the
 * project owner's billing currency), never credits. Two independent switches
 * that are easy to confuse are labelled explicitly: whether the app accepts
 * public sign-ups at all lives in the application's Auth settings; this card
 * only decides WHO may self-register and WHAT they pay.
 */
export default function SelfRegistrationCard({
  projectId, canManage,
}: {
  projectId: string
  canManage: boolean
}) {
  const [config, setConfig] = useState<ProjectBillingConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setConfig(await getProjectBillingConfig(projectId))
    } catch {
      setConfig(null)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => { void load() }, [load])

  const save = async (patch: Partial<ProjectBillingConfig>) => {
    setSaving(true)
    try {
      setConfig(await updateProjectBillingConfig(projectId, patch))
      toast.success('Saved')
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Loader2 className="spin" size={16} />
  if (!config || !config.credit_billing_enabled) return null

  // The project bills in ONE currency (its owner's), so only that currency's
  // fields are shown — the other currency's columns stay untouched.
  const cur = config.currency === 'INR' ? 'inr' : 'usd'
  const symbol = config.currency === 'INR' ? '₹' : '$'
  const feeRegularKey = `self_reg_fee_regular_${cur}_minor` as keyof ProjectBillingConfig
  const feeProKey = `self_reg_fee_pro_${cur}_minor` as keyof ProjectBillingConfig
  const seatKey = `runtime_user_cost_${cur}_minor` as keyof ProjectBillingConfig
  const proSeatKey = `pro_invite_cost_${cur}_minor` as keyof ProjectBillingConfig
  const seatDefault = config.defaults[`default_runtime_user_cost_${cur}_minor`] ?? 0
  const proSeatDefault = config.defaults[`default_pro_invite_cost_${cur}_minor`] ?? 0

  const moneyInput = (
    key: keyof ProjectBillingConfig,
    placeholder: string,
  ) => (
    <input
      type="number" min={0} step="0.01"
      className="form-input"
      value={config[key] === null || config[key] === undefined
        ? '' : (config[key] as number) / 100}
      placeholder={placeholder}
      disabled={!canManage || saving}
      onBlur={(e) => {
        const raw = e.target.value.trim()
        save({ [key]: raw === '' ? null : Math.round(parseFloat(raw) * 100) } as Partial<ProjectBillingConfig>)
      }}
      onChange={(e) => setConfig({
        ...config,
        [key]: e.target.value === '' ? null : Math.round(parseFloat(e.target.value) * 100),
      })}
      style={{ width: '100%', marginTop: 4 }}
    />
  )

  return (
    <div style={{
      padding: '14px', border: '1px solid var(--color-border)',
      borderRadius: 8, marginTop: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <UserPlus size={16} style={{ color: 'var(--color-accent)' }} />
        <strong style={{ fontSize: 14 }}>Self-registration</strong>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          — prices in {config.currency}
        </span>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14 }}>
        <input
          type="checkbox"
          checked={config.self_reg_open_to_pro}
          disabled={!canManage || saving}
          onChange={(e) => save({ self_reg_open_to_pro: e.target.checked })}
          style={{ marginTop: 3 }}
        />
        <span>
          Let Twasta Pro users join without a seat charge
          <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>
            They already pay for their own plan, so nothing is taken from this
            application's wallet when they join.
          </span>
        </span>
      </label>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          App usage fee — regular users ({symbol})
          {moneyInput(feeRegularKey, '0 (free)')}
        </label>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          App usage fee — Pro users ({symbol})
          {moneyInput(feeProKey, '0 (free)')}
        </label>
      </div>

      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
        A fee is charged to the person registering, at checkout, before their
        account gets access — leave both blank to keep registration free. Whether
        the application accepts public sign-ups at all is a separate switch, in
        the application's <strong>Auth settings</strong>.
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginTop: 14 }}>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Per runtime user ({symbol})
          {moneyInput(seatKey, `Platform default (${symbol}${(seatDefault / 100).toFixed(2)})`)}
        </label>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Per Pro invitee ({symbol})
          {moneyInput(proSeatKey, `Platform default (${symbol}${(proSeatDefault / 100).toFixed(2)})`)}
        </label>
      </div>
      {saving && <Button variant="ghost" disabled style={{ marginTop: 8 }}>Saving…</Button>}
    </div>
  )
}
